
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic,
  Brain,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Clock,
  FileText,
  Menu,
  X,
  Shield,
  Scale,
  Play,
  ChevronDown,
  MessageCircle,
  Calendar,
  Lock,
  Smartphone,
  Check,
  Gift,
  ClipboardList,
  DollarSign,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CookieConsent } from '../components/CookieConsent';
import { trackClientEvent } from '../utils/analytics';

export const LandingPage: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // State for Modals
  const [legalModalOpen, setLegalModalOpen] = useState<string | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // State for FAQ
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Função de Scroll Manual para SPA
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const plans = [
    {
      name: 'Básico',
      price: billing === 'monthly' ? 39.90 : 29.90,
      description: 'Organize sua clínica e comece a digitalizar seus atendimentos.',
      features: [
        'Pacientes ilimitados',
        'Prontuário Digital por sessão',
        'Formulários Personalizáveis',
        'Agenda de Sessões',
        'Controle Financeiro'
      ],
      color: 'bg-white border-gray-100',
      btnVariant: 'secondary' as const
    },
    {
      name: 'Pro',
      price: billing === 'monthly' ? 79.90 : 59.90,
      description: 'Potencialize suas sessões com Inteligência Artificial.',
      features: [
        'Tudo do Básico',
        'Diário com entrada por Áudio',
        'Transcrição automática por IA',
        'Resumos clínicos por IA',
        'Relatórios com filtro e PDF'
      ],
      color: 'bg-blue-50/50 border-blue-100 ring-1 ring-blue-200',
      popular: true,
      btnVariant: 'primary' as const
    },
    {
      name: 'Clínico',
      price: billing === 'monthly' ? 99.90 : 74.90,
      description: 'Segurança proativa e gestão completa da equipe.',
      features: [
        'Tudo do Pro',
        'Alertas de Risco 24/7',
        'Análise de sentimento avançada',
        'Gestão de Equipe',
        'Suporte Prioritário'
      ],
      color: 'bg-gradient-to-b from-white to-orange-50 border-orange-100',
      btnVariant: 'cta' as const
    }
  ];

  const faqs = [
    {
      question: "O aplicativo garante o sigilo dos dados do paciente?",
      answer: "Sim. A segurança é nossa prioridade absoluta. Utilizamos criptografia de ponta a ponta em todos os dados. Além disso, estamos 100% em conformidade com a LGPD. Apenas você e seu paciente têm acesso ao conteúdo dos diários e prontuários."
    },
    {
      question: "Como funciona o teste gratuito de 7 dias?",
      answer: "Você pode criar sua conta e acessar todas as funcionalidades do plano Básico imediatamente, sem precisar inserir cartão de crédito. Após 7 dias, você escolhe se deseja assinar um dos planos para continuar utilizando."
    },
    {
      question: "Posso criar formulários diferentes para cada paciente?",
      answer: "Sim! Este é um dos nossos maiores diferenciais. Você pode criar um 'Diário de Ansiedade' para um paciente e um 'Registro de Sono' para outro. Você personaliza as perguntas e escolhe se quer permitir áudio ou não."
    },
    {
      question: "A agenda sincroniza com o Google Calendar?",
      answer: "O Cognitya permite que você gere links de videochamada (Google Meet) e adicione os agendamentos ao seu Google Calendar com um clique, facilitando a organização da sua rotina."
    },
    {
      question: "Existe período de fidelidade?",
      answer: "Não. Você pode cancelar sua assinatura mensal a qualquer momento sem multa. No plano anual, garantimos o desconto de 25% e o serviço ativo pelo período contratado."
    },
    {
      question: "Preciso instalar algum app?",
      answer: "Não! O Cognitya é um PWA (Progressive Web App). Tanto você quanto seus pacientes acessam pelo navegador e podem instalar direto na tela inicial do celular — sem precisar baixar nada pela loja de apps. Funciona inclusive offline."
    },
    {
      question: "O prontuário digital substitui meu prontuário em papel?",
      answer: "O Cognitya é uma ferramenta de apoio à sua prática clínica. As anotações do prontuário digital complementam sua documentação, oferecendo organização, busca por palavras-chave e segurança que o papel não oferece."
    }
  ];

  const ecosystemFeatures = [
    {
      icon: ClipboardList,
      title: "Prontuário Digital",
      desc: "Histórico organizado por sessão com busca inteligente."
    },
    {
      icon: FileText,
      title: "Diário do Paciente",
      desc: "Formulários personalizados com texto ou áudio."
    },
    {
      icon: Mic,
      title: "Transcrição por IA",
      desc: "Áudios viram texto automaticamente."
    },
    {
      icon: Brain,
      title: "Resumos Clínicos",
      desc: "Insights rápidos sobre estado emocional e tarefas."
    },
    {
      icon: AlertTriangle,
      title: "Alertas de Risco",
      desc: "Monitoramento contínuo entre sessões."
    },
    {
      icon: Calendar,
      title: "Agenda Integrada",
      desc: "Sessões sincronizadas com Google Calendar."
    },
    {
      icon: DollarSign,
      title: "Gestão Financeira",
      desc: "Controle de pagamentos e inadimplência."
    },
    {
      icon: Lock,
      title: "Modo Privado",
      desc: "Oculte dados sensíveis em locais públicos."
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const renderLegalContent = () => {
    switch (legalModalOpen) {
      case 'terms':
        return (
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <p><strong>1. Aceitação dos Termos:</strong> Ao criar uma conta no Cognitya, você concorda em utilizar a plataforma como uma ferramenta de apoio à prática clínica, entendendo que ela não substitui o julgamento profissional.</p>
            <p><strong>2. Responsabilidades do Profissional:</strong> O psicólogo é inteiramente responsável pela gestão dos dados de seus pacientes e pela intervenção em casos de alertas de risco gerados pela IA.</p>
            <p><strong>3. Uso da Inteligência Artificial:</strong> As transcrições e análises são geradas por algoritmos. Embora precisos, podem conter erros de interpretação e devem ser revisados pelo profissional.</p>
            <p><strong>4. Cancelamento:</strong> Você pode cancelar sua assinatura a qualquer momento. O acesso permanecerá ativo até o fim do ciclo de faturamento atual.</p>
          </div>
        );
      case 'privacy':
        return (
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <p><strong>1. Coleta de Dados:</strong> Coletamos apenas os dados estritamente necessários para o funcionamento do serviço: nome, CRP, e-mail e os registros clínicos inseridos.</p>
            <p><strong>2. Uso das Informações:</strong> Seus dados não são vendidos para terceiros. Eles são utilizados exclusivamente para fornecer as funcionalidades do software e melhorar a experiência do usuário.</p>
            <p><strong>3. Armazenamento:</strong> Utilizamos servidores seguros com criptografia de ponta a ponta. Seus dados estão protegidos contra acesso não autorizado.</p>
          </div>
        );
      case 'lgpd':
        return (
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <p><strong>1. Conformidade:</strong> O Cognitya atua em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018).</p>
            <p><strong>2. Titularidade:</strong> Os dados pertencem aos pacientes e psicólogos. A Cognitya atua como operadora dos dados.</p>
            <p><strong>3. Seus Direitos:</strong> Você tem direito a solicitar acesso, correção, portabilidade ou exclusão dos seus dados a qualquer momento através do painel de conta ou suporte.</p>
            <p><strong>4. Encarregado de Dados (DPO):</strong> Para questões relacionadas à privacidade, entre em contato através de suporte@cognitya.com.</p>
          </div>
        );
      default:
        return null;
    }
  };

  const getLegalTitle = () => {
    if (legalModalOpen === 'terms') return 'Termos de Uso';
    if (legalModalOpen === 'privacy') return 'Política de Privacidade';
    if (legalModalOpen === 'lgpd') return 'Compliance LGPD';
    return '';
  };

  return (
    <div className="min-h-screen bg-bg font-sans text-text selection:bg-primary/20 overflow-x-hidden w-full max-w-full">
      <CookieConsent />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 transition-all">
        <div className="max-w-7xl mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/logo.png" alt="Cognitya Logo" className="w-10 h-10 rounded-xl" width="40" height="40" fetchPriority="high" />
            <span className="font-display font-bold text-xl text-secondary tracking-tight">Cognitya</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('funcionalidades')} aria-label="Ir para seção de funcionalidades" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors bg-transparent border-none cursor-pointer">Funcionalidades</button>
            <button onClick={() => scrollToSection('beneficios')} aria-label="Ir para seção de benefícios" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors bg-transparent border-none cursor-pointer">Benefícios</button>
            <button onClick={() => scrollToSection('precos')} aria-label="Ir para seção de planos e preços" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors bg-transparent border-none cursor-pointer">Planos</button>
            <button onClick={() => scrollToSection('faq')} aria-label="Ir para perguntas frequentes" className="text-sm font-medium text-gray-600 hover:text-primary transition-colors bg-transparent border-none cursor-pointer">Dúvidas</button>
            <Link to="/login?ref=botao_topo_entrar" onClick={() => trackClientEvent('clique_cta', { cta_id: 'botao_topo_entrar' })}>
              <Button variant="primary" size="sm" className="px-6 rounded-xl shadow-lg shadow-primary/20">
                Entrar / Cadastrar
              </Button>
            </Link>
          </div>

          <button
            className="md:hidden text-secondary"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-100 p-6 space-y-4 animate-fade-in absolute w-full shadow-xl">
            <button className="block text-sm font-medium text-gray-600 w-full text-left" onClick={() => scrollToSection('funcionalidades')}>Funcionalidades</button>
            <button className="block text-sm font-medium text-gray-600 w-full text-left" onClick={() => scrollToSection('beneficios')}>Benefícios</button>
            <button className="block text-sm font-medium text-gray-600 w-full text-left" onClick={() => scrollToSection('precos')}>Planos</button>
            <button className="block text-sm font-medium text-gray-600 w-full text-left" onClick={() => scrollToSection('faq')}>Dúvidas</button>
            <Link to="/login?ref=menu_mobile_entrar" className="block w-full" onClick={() => trackClientEvent('clique_cta', { cta_id: 'menu_mobile_entrar' })}>
              <Button variant="primary" className="w-full justify-center">Acessar Plataforma</Button>
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-32 pb-8 md:pb-12 px-6 overflow-hidden relative">
        <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10 flex flex-col items-center">

          {/* Badge */}
          <div className="inline-flex items-start sm:items-center gap-2 bg-gray-100 border border-gray-200 rounded-xl sm:rounded-full px-4 py-2 sm:py-1.5 mb-4 shadow-sm animate-fade-in text-left sm:text-center">
            <Gift size={16} className="text-primary shrink-0 mt-[2px] sm:mt-0" />
            <span className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide leading-tight mt-[1px]">Teste Grátis por 7 dias · sem cartão de crédito</span>
          </div>
          
          <div className="text-[11px] sm:text-xs md:text-sm font-bold text-gray-500 mb-4 tracking-widest uppercase flex justify-center text-center text-balance w-full px-2 mx-auto">
            A PLATAFORMA IDEAL PARA PSIQUIATRAS, PSICÓLOGOS, TERAPEUTAS E CLÍNICAS GERAIS
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-[4rem] font-display font-extrabold text-secondary mb-5 leading-tight lg:leading-[1.1] tracking-tight text-balance max-w-5xl mx-auto px-2 md:px-0">
            <span>Pare de usar o WhatsApp para</span> <br className="hidden md:block" /> 
            <span className="text-primary underline decoration-primary/30 decoration-8 underline-offset-4">acompanhar seus pacientes</span>.
          </h1>

          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed font-medium px-4">
            O Cognitya organiza relatos, transcreve áudios com IA e alerta você em caso de risco — tudo em uma plataforma feita para cuidar de quem cuida.
          </p>

          <div className="flex flex-col items-center justify-center gap-2 mb-10 w-full">
            <Link to="/login?ref=botao_principal_hero" className="w-[90%] sm:w-auto" onClick={() => trackClientEvent('clique_cta', { cta_id: 'botao_principal_hero' })}>
              <Button size="lg" variant="cta" className="w-full flex items-center justify-center shadow-2xl shadow-cta/30 text-[17px] md:text-xl font-bold px-6 sm:px-10 py-3 sm:py-0 min-h-[72px] sm:h-20 rounded-xl flex-col sm:flex-row gap-0.5 sm:gap-0 hover:scale-105 transition-transform">
                <span>Começar grátis agora</span>
                <span className="whitespace-nowrap sm:ml-2 text-sm sm:text-xl opacity-90 sm:opacity-100 font-medium sm:font-bold">(leva 1 minuto)</span>
                <ArrowRight className="ml-2 w-5 h-5 shrink-0 hidden sm:block" />
              </Button>
            </Link>
            <span className="text-xs sm:text-sm font-bold text-gray-400 mt-2">Acesso imediato. Sem cartão de crédito.</span>
          </div>

          <div id="video-demo" className="w-full max-w-5xl rounded-3xl shadow-2xl border-4 md:border-8 border-gray-100 bg-black aspect-video relative group animate-fade-in z-20 mx-auto overflow-hidden">
             <div 
               onClick={() => setVideoModalOpen(true)}
               className="absolute inset-0 w-full h-full cursor-pointer group"
               role="button"
               aria-label="Reproduzir vídeo de demonstração"
             >
               {/* High-quality Thumbnail image with lazy loading and scale effect */}
               <picture>
                 <source media="(max-width: 640px)" srcSet="/video-thumbnail-mobile.webp" />
                 <img 
                   src="/video-thumbnail.webp"
                   alt="Cognitya - Demonstração Completa"
                   fetchPriority="high"
                   className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
                 />
               </picture>
               
               {/* Elegant Overlay with Gradient and Glassmorphism Play Button */}
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center transition-all duration-300 group-hover:bg-black/40">
                 
                 {/* High-end Pulse Play Button */}
                 <div className="relative flex items-center justify-center">
                   {/* Multiple pulse ring effects */}
                   <div className="absolute w-24 h-24 rounded-full bg-primary/30 animate-ping opacity-75 duration-1000"></div>
                   <div className="absolute w-20 h-20 rounded-full bg-primary/50 animate-pulse"></div>
                   
                   {/* Main Play Icon container */}
                   <div className="relative w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                     <Play size={28} className="fill-current ml-1" />
                   </div>
                 </div>
                 
                 {/* Floating Info Tag */}
                 <div className="absolute bottom-6 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-white text-sm font-bold flex items-center gap-2 transform translate-y-2 opacity-90 transition-all group-hover:translate-y-0 group-hover:opacity-100 shadow-lg">
                   <Play size={14} className="fill-current" /> Assistir Demonstração Completa (4 min)
                 </div>
               </div>
             </div>
          </div>

          <div className="mt-10 mb-2">
            <Link to="/login?ref=botao_abaixo_video" onClick={() => trackClientEvent('clique_cta', { cta_id: 'botao_abaixo_video' })}>
              <Button className="rounded-xl shadow-xl font-bold bg-secondary text-white hover:bg-secondary/90 border-transparent px-8 sm:px-12 py-4 sm:py-5 h-auto text-base sm:text-lg hover:scale-105 transition-all">
                Quero experimentar agora
                <ArrowRight className="ml-2 w-5 h-5 shrink-0 inline-block" />
              </Button>
            </Link>
          </div>

          {/* Social Proof */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-4 sm:gap-8 text-left sm:text-center mt-10 bg-white border-2 border-primary/10 px-8 py-5 rounded-2xl sm:rounded-full shadow-lg">
            <div className="flex items-center gap-3">
              <Shield size={24} className="text-primary shrink-0" />
              <span className="text-sm sm:text-base font-bold text-secondary">LGPD Compliant</span>
            </div>
            <div className="hidden sm:block w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
            <div className="flex items-center gap-3">
              <Lock size={24} className="text-primary shrink-0" />
              <span className="text-sm sm:text-base font-bold text-secondary">Criptografia de ponta a ponta</span>
            </div>
            <div className="hidden sm:block w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
            <div className="flex items-center gap-3">
              <Smartphone size={24} className="text-primary shrink-0" />
              <span className="text-sm sm:text-base font-bold text-secondary">Funciona sem instalar app</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE PAIN */}
      <section className="py-16 md:py-20 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-6 pt-10">
          <div className="text-center mb-16">
            <span className="text-red-500 font-bold tracking-widest uppercase text-sm mb-4 block">Seja honesto...</span>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-secondary mb-6">Quantas vezes isso já aconteceu com você?</h2>
          </div>

          <div className="space-y-4 max-w-2xl mx-auto">
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500 shrink-0 mt-1">
                  <X size={20} />
                </div>
                <p className="text-lg text-gray-700 leading-relaxed font-bold">Você já perdeu um relato importante no WhatsApp?</p>
             </div>
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500 shrink-0 mt-1">
                  <X size={20} />
                </div>
                <p className="text-lg text-gray-700 leading-relaxed font-bold">Já teve que ouvir áudios de 6 minutos fora de hora?</p>
             </div>
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500 shrink-0 mt-1">
                  <X size={20} />
                </div>
                <p className="text-lg text-gray-700 leading-relaxed font-bold">Já chegou na sessão sem saber exatamente o que aconteceu na semana do paciente?</p>
             </div>
             <div className="bg-white p-6 rounded-2xl border border-red-200 flex items-start gap-4 ring-2 ring-red-50 shadow-md transform scale-[1.02]">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0 mt-1">
                  <AlertTriangle size={20} />
                </div>
                <p className="text-lg font-bold text-red-900 leading-relaxed">Já ficou com medo de ignorar um sinal de risco ou ideação?</p>
             </div>
          </div>
          
          <p className="text-center text-xl text-gray-500 mt-16 font-medium max-w-2xl mx-auto">
            A verdade é que a maior parte da terapia acontece entre as sessões — mas quase sempre sem qualquer tipo organização formal.
          </p>
        </div>
      </section>

      {/* 3. THE BIG IDEA (Contrasting Breather) */}
      <section className="py-20 md:py-24 bg-secondary text-white relative">
        <div className="absolute inset-0 overflow-hidden">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[600px] bg-primary/20 blur-[150px] rounded-full"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-display font-bold leading-tight mb-8">A terapia não termina quando a sessão acaba.</h2>
          <p className="text-xl md:text-2xl text-white/80 leading-relaxed font-light max-w-2xl mx-auto">
            É entre uma sessão e outra que o paciente enfrenta gatilhos, registra pensamentos automáticos e toma decisões importantes. O Cognitya foi feito para cobrir exatamente esse espaço invisível.
          </p>
        </div>
      </section>

      {/* 4. SOLUTION PRESENTATION & 5. HOW IT WORKS */}
      <section id="beneficios" className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">Conheça o Cognitya</span>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-secondary mb-6">O ecossistema definitivo desenvolvido para TCC</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Nossa plataforma interliga <strong>Paciente</strong> (relatos fáceis e seguros), a <strong>Inteligência artificial</strong> (análise e transcrição completa) e o <strong>Terapeuta</strong> (com insights mastigados e validados).
            </p>
          </div>

          <div className="text-center mb-16">
            <h3 className="text-2xl font-bold text-secondary mb-12">Um fluxo simples que transforma sua clínica</h3>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-4 gap-8 mb-16 relative">
            {/* Thread line */}
            <div className="hidden md:block absolute top-[45px] left-[10%] right-[10%] h-0.5 bg-gray-100 z-0"></div>

            <div className="relative z-10 bg-white pt-2 text-center group">
               <div className="w-20 h-20 mx-auto bg-gray-50 border border-gray-200 rounded-2xl flex items-center justify-center text-primary mb-6 shadow-sm group-hover:scale-110 transition-transform group-hover:bg-primary group-hover:text-white">
                  <span className="text-2xl font-bold font-display">1</span>
               </div>
               <h3 className="text-lg font-bold text-secondary mb-3">Crie tarefas terapêuticas</h3>
               <p className="text-sm text-gray-500">Monte diários guiados em cliques: monitoramento de humor, RPDs, sono, ansiedade.</p>
            </div>

            <div className="relative z-10 bg-white pt-2 text-center group">
               <div className="w-20 h-20 mx-auto bg-gray-50 border border-gray-200 rounded-2xl flex items-center justify-center text-primary mb-6 shadow-sm group-hover:scale-110 transition-transform group-hover:bg-primary group-hover:text-white">
                  <span className="text-2xl font-bold font-display">2</span>
               </div>
               <h3 className="text-lg font-bold text-secondary mb-3">O paciente relata</h3>
               <p className="text-sm text-gray-500">Ele preenche via áudio rápido pelo celular em ambiente isolado do WhatsApp.</p>
            </div>

            <div className="relative z-10 bg-white pt-2 text-center group">
               <div className="w-20 h-20 mx-auto bg-gray-50 border border-gray-200 rounded-2xl flex items-center justify-center text-accent mb-6 shadow-sm group-hover:scale-110 transition-transform group-hover:bg-accent group-hover:text-white">
                  <span className="text-2xl font-bold font-display">3</span>
               </div>
               <h3 className="text-lg font-bold text-secondary mb-3">A IA organiza tudo</h3>
               <p className="text-sm text-gray-500">Áudios gigantes são transcritos, temas críticos isolados e o padrão emocional é destilado.</p>
            </div>

            <div className="relative z-10 bg-white pt-2 text-center group">
               <div className="w-20 h-20 mx-auto bg-gray-50 border border-gray-200 rounded-2xl flex items-center justify-center text-cta mb-6 shadow-sm group-hover:scale-110 transition-transform group-hover:bg-cta group-hover:text-white">
                  <span className="text-2xl font-bold font-display">4</span>
               </div>
               <h3 className="text-lg font-bold text-secondary mb-3">Sessão preparada</h3>
               <p className="text-sm text-gray-500">Antes da consulta, você apenas lê o resumo objetivo para focar no que fará o paciente avançar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. WHATSAPP ANTAGONIST */}
      <section className="py-16 md:py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-secondary mb-6">Pare de usar o WhatsApp como prontuário clínico.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
             {/* WhatsApp BAD */}
             <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm opacity-90">
                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-100">
                   <div className="w-12 h-12 bg-red-100 text-red-500 rounded-xl flex items-center justify-center">
                     <MessageCircle size={24} />
                   </div>
                   <h3 className="text-2xl font-bold text-gray-800">No WhatsApp</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex gap-3 text-gray-600"><X size={20} className="text-red-400 shrink-0 mt-0.5" /> Mensagens vitais misturadas com demandas pessoais</li>
                  <li className="flex gap-3 text-gray-600"><X size={20} className="text-red-400 shrink-0 mt-0.5" /> Áudios longos desorganizados (impossível buscar depoimento depois)</li>
                  <li className="flex gap-3 text-gray-600"><X size={20} className="text-red-400 shrink-0 mt-0.5" /> Se o celular for subtraído, o sigilo profissional dos seus clientes vai junto</li>
                  <li className="flex gap-3 text-gray-600"><X size={20} className="text-red-400 shrink-0 mt-0.5" /> Histórico destruído instantaneamente se o indivíduo apagar a conversa</li>
                </ul>
             </div>

             {/* Cognitya GOOD */}
             <div className="bg-white p-8 rounded-3xl border-2 border-primary shadow-xl scale-100 md:scale-105 relative z-10">
                <div className="absolute -top-4 -right-4 bg-primary text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">Padrão Clínico</div>
                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-100">
                   <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                     <img src="/logo.png" alt="Cognitya Logo" className="w-10 h-10 rounded-xl grayscale" style={{ filter: 'invert(37%) sepia(93%) saturate(1005%) hue-rotate(188deg) brightness(87%) contrast(92%)'}} width="40" height="40" loading="lazy" />
                   </div>
                   <h3 className="text-2xl font-bold text-secondary">No Cognitya</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex gap-3 text-gray-700 font-medium"><CheckCircle2 size={20} className="text-primary shrink-0 mt-0.5" /> Registros clínicos organizados por paciente</li>
                  <li className="flex gap-3 text-gray-700 font-medium"><CheckCircle2 size={20} className="text-primary shrink-0 mt-0.5" /> Busca instantânea por todo o histórico gravado e transcrito</li>
                  <li className="flex gap-3 text-gray-700 font-medium"><CheckCircle2 size={20} className="text-primary shrink-0 mt-0.5" /> Servidores protegidos por criptografia de ponta a ponta</li>
                  <li className="flex gap-3 text-gray-700 font-medium"><CheckCircle2 size={20} className="text-primary shrink-0 mt-0.5" /> Histórico estruturado imutável associado às sessões da semana</li>
                </ul>
             </div>
          </div>
        </div>
      </section>

      {/* 7. POWER OF AI & 8. RISK ALERTS */}
      <section id="funcionalidades" className="py-16 md:py-24 bg-secondary text-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          
          {/* AI Magic */}
          <div className="grid md:grid-cols-2 gap-16 items-center mb-32">
             <div className="order-2 md:order-1">
               <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative">
                 <div className="flex items-center gap-3 border-b border-white/10 pb-4 mb-4 text-sm font-bold opacity-80">
                   <Brain size={18} className="text-primary-light" /> Analisador Semântico
                 </div>
                 <div className="space-y-4">
                   <div className="bg-white/5 p-4 rounded-xl border-l-4 border-gray-500">
                      <div className="text-xs text-gray-400 mb-2">Áudio bruto (03m:42s) recebido</div>
                      <div className="flex gap-2">
                        <div className="w-full h-8 bg-gray-500/20 rounded-md flex items-center justify-center opacity-50"><Mic size={14} className="animate-pulse" /></div>
                      </div>
                   </div>
                   <div className="flex justify-center"><ArrowRight size={20} className="text-primary rotate-90" /></div>
                   <div className="bg-primary/20 border border-primary/30 p-4 rounded-xl shadow-glow">
                      <div className="flex justify-between items-start mb-2">
                         <span className="text-xs font-bold text-primary-light bg-primary/30 px-2 py-1 rounded">Resumo IA</span>
                         <span className="text-xs text-accent font-mono border border-accent/20 px-2 py-1 rounded">Humor: Irritadiço</span>
                      </div>
                      <p className="text-sm font-medium text-white/90">O paciente apresentou pico de irritabilidade. Nível de ansiedade relatado: 8/10. Tentativas de regulação se mostraram falhas inicialmente mas melhoraram com RPD.</p>
                   </div>
                 </div>
               </div>
             </div>
             
             <div className="order-1 md:order-2 text-left space-y-6">
                <h2 className="text-3xl md:text-5xl font-display font-bold text-balance">Seu paciente fala.<br/>A IA transforma isso em clareza.</h2>
                <p className="text-xl text-white/70 leading-relaxed font-medium">
                  Você não precisa mais ouvir áudios longos infinitos. A inteligência artificial extrai o que importa de dezenas de minutos, poupando seu tempo para focar na intervenção.
                </p>
                <ul className="space-y-4 text-white/80">
                  <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Check size={16} className="text-primary-light"/></div> A IA transcreve palavras e entonações automaticamente</li>
                  <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Check size={16} className="text-primary-light"/></div> Identifica padrões emocionais de humor e cognição</li>
                  <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Check size={16} className="text-primary-light"/></div> Gera um resumo exato focado do que tratar no próximo encontro</li>
                </ul>
             </div>
          </div>

          <hr className="border-white/10 border-t-2 w-full max-w-sm mx-auto mb-32" />

          {/* Bodyguard (Risk Alerts) */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
             <div className="text-left space-y-6">
                <div className="inline-flex bg-red-500/20 text-red-400 font-bold px-3 py-1 text-sm rounded-full border border-red-500/30 items-center gap-2">
                  <Shield size={14} /> Alertas de Risco 24/7
                </div>
                <h2 className="text-3xl md:text-5xl font-display font-bold text-red-400">E se você perder um sinal de risco?</h2>
                <p className="text-xl text-white/80 leading-relaxed font-medium">
                  Um relato intenso pode chegar de madrugada. Um pedido de ajuda urgente pode passar despercebido.
                </p>
                <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl space-y-3 mt-4">
                   <h4 className="font-bold text-white mb-3">Isso muda com o Cognitya:</h4>
                   <div className="flex items-center gap-3 text-red-200/90 font-bold"><AlertTriangle size={18} className="shrink-0" /> Você recebe um alerta grave e imediato</div>
                   <div className="flex items-center gap-3 text-red-200/90 font-bold"><AlertTriangle size={18} className="shrink-0" /> Monitoramento contínuo de agressão e ideação</div>
                   <div className="flex items-center gap-3 text-red-200/90 font-bold"><AlertTriangle size={18} className="shrink-0" /> Ação imediata, reduzindo tragédias e trazendo resguardo jurídico.</div>
                </div>
             </div>

             <div className="relative flex justify-center">
                {/* Notification Mockup */}
                <div className="bg-white text-secondary w-full max-w-md rounded-3xl shadow-2xl overflow-hidden shadow-red-500/20">
                  <div className="bg-red-500 p-4 font-bold text-white flex items-center gap-3 shadow-sm z-10 relative">
                     <AlertTriangle size={24} /> ALERTA DE RISCO DETECTADO
                  </div>
                  <div className="p-6">
                     <p className="text-sm font-bold text-gray-500 mb-1">Notificação Imediata - SMS / App</p>
                     <p className="text-gray-800 text-lg mb-4 font-medium leading-snug">
                       O paciente <strong className="text-red-500">Mateus S.</strong> disparou um gatilho de risco severo através da transcrição do diário recém-enviado.
                     </p>
                     <p className="text-sm border-l-4 border-red-300 pl-3 italic text-gray-600 mb-6 font-medium">"...eu juro que eu não vejo sentido de segurar as pontas mais, não vejo mais o amanhã..."</p>
                     
                     <div className="flex gap-3">
                       <button className="flex-1 bg-red-500 hover:bg-red-600 transition-colors text-white py-3 rounded-xl font-bold font-sm flex items-center justify-center gap-2">
                          Agir Agora
                       </button>
                     </div>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 9. FEATURES GRID (ECOSYSTEM) */}
      <section className="py-16 md:py-20 bg-white relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display font-bold text-secondary mb-4">Tudo que sua clínica precisa em um só lugar.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosystemFeatures.map((feat, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                  <feat.icon size={20} />
                </div>
                <h3 className="font-bold text-secondary text-lg mb-2">{feat.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-16 md:py-24 px-6 bg-gray-50 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-secondary mb-4">Escolha o plano ideal para sua clínica</h2>
            
            {/* ANCHORAGE */}
            <p className="text-xl font-bold text-gray-500 mb-6 max-w-2xl mx-auto text-balance">
              Menos que o <span className="text-green-600 underline decoration-green-300">valor de 1 sessão</span>, para organizar os dados e blindar sua prática clínica.
            </p>

            <div className="bg-purple-100 inline-block px-4 py-2 rounded-lg mb-6 shadow-sm border border-purple-200">
              <span className="text-purple-700 text-sm font-bold flex items-center gap-2">
                <Gift size={16} /> Comece hoje com 7 dias grátis. Sem cartão de crédito.
              </span>
            </div>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <span className={`text-sm font-bold ${billing === 'monthly' ? 'text-gray-800' : 'text-gray-400'}`}>Mensal</span>
              <button
                onClick={() => setBilling(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                aria-label={`Mudar para plano ${billing === 'monthly' ? 'anual' : 'mensal'}`}
                role="switch"
                aria-checked={billing === 'yearly'}
                className={`w-14 h-7 rounded-full p-1 transition-colors duration-300 ${billing === 'yearly' ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${billing === 'yearly' ? 'translate-x-7' : 'translate-x-0'}`} />
              </button>
              <span className={`text-sm font-bold ${billing === 'yearly' ? 'text-gray-800' : 'text-gray-400'}`}>
                Anual <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full ml-1 font-extrabold animate-pulse">-25% OFF</span>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {plans.map((plan) => (
              <div key={plan.name} className={`relative bg-white rounded-[24px] p-8 border transition-all duration-300 ${plan.popular ? 'shadow-2xl scale-105 z-10 border-primary/30 ring-4 ring-primary/5' : 'shadow-lg border-gray-100 hover:border-primary/20'}`}>
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                    Mais Popular
                  </div>
                )}

                <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-600 mb-6 min-h-[40px] font-medium leading-snug">{plan.description}</p>

                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-sm text-gray-500 font-bold">R$</span>
                  <span className="text-5xl font-display font-bold text-gray-900">{plan.price.toFixed(2).replace('.', ',')}</span>
                  <span className="text-sm text-gray-500 font-bold">/mês</span>
                </div>

                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm text-gray-700 font-medium">
                      <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${plan.popular ? 'text-primary' : 'text-gray-400'}`} />
                      {feature}
                    </div>
                  ))}
                </div>

                <Link 
                  to={`/login?ref=${plan.name === 'Básico' ? 'plano_basico' : plan.name === 'Pro' ? 'plano_pro' : 'plano_clinico'}`} 
                  className="block"
                  onClick={() => {
                    const ctaId = plan.name === 'Básico' ? 'plano_basico' : plan.name === 'Pro' ? 'plano_pro' : 'plano_clinico';
                    trackClientEvent('clique_cta', { cta_id: ctaId });
                  }}
                >
                  <Button variant={plan.btnVariant} className="w-full h-12 text-base shadow-lg">
                    Começar Teste Grátis
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-16 bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-sm relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 group transition-all duration-300 hover:border-primary/30">
            <div className="absolute inset-0 bg-primary/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out z-0"></div>
            <div className="relative z-10 text-center md:text-left md:basis-2/3">
              <h3 className="text-2xl font-bold text-secondary mb-3 flex flex-col md:flex-row items-center gap-3">
                 <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shrink-0">
                    <Users size={24} />
                 </div>
                 Possui uma clínica com vários profissionais?
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed font-medium md:pl-15">
                 Entendemos a complexidade de grandes equipes. Oferecemos uma <strong>solução customizada multiplataforma</strong> inteligente e centralizada.
              </p>
            </div>
            <div className="relative z-10 md:basis-1/3 flex justify-center md:justify-end w-full md:w-auto">
              <a
                href="mailto:contato@cognitya.com"
                className="inline-flex items-center justify-center gap-2 bg-secondary text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all w-full md:w-auto min-w-[240px]"
              >
                Falar com Consultor
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-20 px-6 bg-primary/5 border-t border-primary/10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-primary text-sm font-bold uppercase tracking-widest mb-2 block">Tira-Dúvidas</span>
            <h2 className="text-3xl font-display font-bold text-secondary mb-4">Perguntas Frequentes</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Tudo o que você precisa saber antes de digitalizar sua clínica.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                onClick={() => toggleFaq(index)}
                className={`bg-white rounded-2xl p-6 border transition-all duration-300 cursor-pointer ${openFaqIndex === index ? 'shadow-md border-primary/30' : 'shadow-sm border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex justify-between items-center gap-4">
                  <h3 className={`font-bold text-lg transition-colors leading-snug ${openFaqIndex === index ? 'text-primary' : 'text-secondary'}`}>
                    {faq.question}
                  </h3>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${openFaqIndex === index ? 'bg-primary text-white rotate-180' : 'bg-gray-100 text-gray-400'}`}>
                    <ChevronDown size={18} />
                  </div>
                </div>

                <div className={`grid transition-all duration-500 ease-in-out ${openFaqIndex === index ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <p className="text-gray-600 leading-relaxed text-base border-t border-gray-100 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">Ainda tem dúvidas?</p>
            <button onClick={() => window.open('mailto:suporte@cognitya.com')} className="inline-flex items-center gap-2 text-primary font-bold hover:underline">
              <MessageCircle size={18} /> Fale com nosso suporte
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-primary relative z-20 text-white text-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-display font-black mb-6 leading-tight tracking-tight shadow-primary/20 text-balance">Quantos sinais de risco importantes você já ignorou sem perceber?</h2>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl font-medium">
            A responsabilidade da clínica é grande demais para ficar misturada com mensagens pessoais do WhatsApp.
          </p>
          <Link to="/login?ref=botao_final_cta" className="w-full sm:w-auto" onClick={() => trackClientEvent('clique_cta', { cta_id: 'botao_final_cta' })}>
            <Button size="lg" variant="cta" className="flex items-center justify-center flex-col sm:flex-row gap-0.5 sm:gap-2 px-8 sm:px-12 py-3 sm:py-5 min-h-[72px] text-xl font-black shadow-2xl shadow-black/30 w-full rounded-xl hover:scale-105 transition-transform bg-white !text-secondary border-2 border-secondary/10">
              <span>Começar grátis agora</span>
              <span className="whitespace-nowrap text-sm sm:text-xl font-bold opacity-80 sm:opacity-100">(leva 1 minuto)</span>
            </Button>
          </Link>
          <p className="mt-8 font-bold text-white/70 text-sm uppercase tracking-wider">7 dias grátis · Acesso imediato · Sem cartão de crédito</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 text-gray-600 py-12 px-6 relative z-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8 mb-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Cognitya Logo" className="w-8 h-8 rounded-lg" width="32" height="32" loading="lazy" />
              <span className="font-display font-bold text-xl text-secondary">Cognitya</span>
            </div>
            <p className="text-sm max-w-xs text-gray-500">
              Plataforma de inteligência artificial para profissionais de TCC. Prontuário, diário do paciente e alertas de risco — integrados.
            </p>
          </div>
          <div>
            <h4 className="text-secondary font-bold mb-4">Produto</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li><button onClick={() => scrollToSection('funcionalidades')} className="hover:text-primary transition-colors bg-transparent border-none cursor-pointer text-left">Funcionalidades</button></li>
              <li><button onClick={() => scrollToSection('precos')} className="hover:text-primary transition-colors bg-transparent border-none cursor-pointer text-left">Preços</button></li>
              <li><button onClick={() => scrollToSection('faq')} className="hover:text-primary transition-colors bg-transparent border-none cursor-pointer text-left">FAQ (Dúvidas)</button></li>
              <li><Link to="/login?ref=link_rodape_login" className="hover:text-primary transition-colors" onClick={() => trackClientEvent('clique_cta', { cta_id: 'link_rodape_login' })}>Login</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-secondary font-bold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li><Link to="/privacy" className="hover:text-primary transition-colors">Política de Privacidade</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors">Termos de Uso</Link></li>
              <li><button onClick={() => setLegalModalOpen('lgpd')} className="hover:text-primary transition-colors text-left bg-transparent border-none cursor-pointer">Compliance LGPD</button></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-8 border-t border-gray-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-400">
            <span>&copy; 2026 Cognitya. Todos os direitos reservados. CNPJ: 47.148.339/0001-01</span>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacidade</Link>
              <Link to="/terms" className="hover:text-primary transition-colors">Termos</Link>
              <a href="mailto:suporte@cognitya.com" className="hover:text-primary transition-colors">Contato</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal (Lightbox) */}
      {
        legalModalOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl relative max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-6 shrink-0 pb-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-2">
                  {legalModalOpen === 'lgpd' ? <Shield size={24} className="text-primary" /> : <Scale size={24} className="text-primary" />}
                  {getLegalTitle()}
                </h2>
                <button
                  onClick={() => setLegalModalOpen(null)}
                  className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="overflow-y-auto pr-2 custom-scrollbar">
                {renderLegalContent()}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100 shrink-0">
                <Button onClick={() => setLegalModalOpen(null)} variant="secondary" className="w-full">
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Video Modal */}
      {
        videoModalOpen && (
          <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="w-full max-w-4xl relative bg-black border border-white/10 rounded-2xl shadow-2xl overflow-hidden aspect-video flex items-center justify-center shadow-black/80">
              <button
                onClick={() => setVideoModalOpen(false)}
                className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-white/20 hover:scale-105 transition-all"
                aria-label="Fechar vídeo"
              >
                <X size={20} />
              </button>

              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/7BQv8DIBF2k?autoplay=1&mute=0&rel=0&enablejsapi=1"
                title="Vídeo de Demonstração Cognitya"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        )
      }
    </div>
  );
};
